import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import Select from "react-select";
import useProcurementCart from "./cart/cartStore";
import EmptyValue from "../../../components/EmptyValue";

// Shared between ProcurementView.jsx (Overall + New) and ReorderProcurementView.jsx
// (Reorder) — the three tabs read the SAME GET /api/inventory/shortages row shape, so
// the view-slicing logic below lives in exactly one place rather than drifting across
// two components that render very differently (Overall/New share one table shape;
// Reorder is a structurally different table, prototype-matched — see
// ReorderProcurementView.jsx's header comment).

export const TABS = [
    { key: "overall", label: "Overall", path: "/v2/inventory/procurement" },
    { key: "new", label: "New", path: "/v2/inventory/procurement/new" },
    { key: "reorder", label: "Reorder", path: "/v2/inventory/procurement/reorder" },
];

// One purchase decision, three ways of looking at it — an in-page tab strip, not three
// sidebar entries (the console's sidebar convention is one entry per section; see
// nav-config.js).
export function ProcurementTabs() {
    const location = useLocation();
    return (
        <nav className="mt-4 flex gap-1 border-b border-[var(--ops-carbon-200)]" aria-label="Procurement views">
            {TABS.map((tab) => {
                const active = location.pathname === tab.path;
                return (
                    <Link
                        key={tab.key}
                        to={tab.path}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${active
                            ? "border-[var(--ops-emerald-500)] text-[var(--ops-emerald-600)]"
                            : "border-transparent text-[var(--ops-carbon-500)] hover:text-[var(--ops-carbon-900)]"
                            }`}
                    >
                        {tab.label}
                    </Link>
                );
            })}
        </nav>
    );
}

// Prototype-matched (~/Desktop/IventoryDashboard/app.js MEDICINE_CHIPS) — replaces the
// earlier Needs-ordering/Stock-unknown/Demand-unknown set. "Placed" is the prototype's
// OWN grouping (Requested OR Delayed as one bucket), not an invented third status — match
// it, don't add a fourth chip. Deliberate loss (product's call): Stock-unknown/Demand-
// unknown rows are no longer isolatable in one click — they still appear (status Pending)
// under Complete view and Pending, just not filterable on their own anymore.
export const FILTER_CHIPS = [
    { id: "all", label: "Complete view" },
    { id: "pending", label: "Pending" },
    { id: "placed", label: "Placed" },
];
export const PARAM_FILTER = "filter";
// Server-side pagination + medicine-name search (2026-10-26) — see useProcurementTable.
export const PARAM_PAGE = "page";
export const PARAM_SEARCH = "q";
// Also the most eVital stock reads one page view can cost (pillup-api reads stock for the
// rows on the page only).
export const SHORTAGES_PAGE_SIZE = 10;
// Rows-per-page choice (up to the API's cap of 100) and the "only shortages" toggle, both in the URL.
export const PARAM_PAGE_SIZE = "size";
export const PARAM_SHORT = "short";
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
// Predicted-demand lookahead (days) — the purchase manager's dropdown. Affects Reorder and the
// Reorder part of Overall only; New (committed demand) ignores it. 15 is the API's default.
export const PARAM_LOOKAHEAD = "days";
export const LOOKAHEAD_OPTIONS = [3, 7, 10, 15];
export const DEFAULT_LOOKAHEAD_DAYS = 15;

// The prototype's "Placed" bucket, ported verbatim: status === requested || status ===
// delayed. Our status values are capitalized ("Requested"/"Delayed", §Phase-7-UI) where
// the prototype's were lowercase — same two-way grouping, not a new one.
export const isPlacedStatus = (status) => status === "Requested" || status === "Delayed";

// New/Reorder require the backend's Phase 7 lapsing half to be on (`row.purchase` only
// exists when it is) — without it there is no A/B split to show, so those two views
// render an explanatory empty state instead of silently showing nothing.
//
// A medicine appears on a view whenever it has demand in that slice, even when the
// resulting purchase figure is 0 (e.g. Metformin on New: N=30 but tablets=0 because
// onHand already covers it). A demandUnknown row cannot be attributed to one slice (the
// backend's demandUnknown flag is a single merged boolean across N/A/B — see
// calculateShortages in pillup-api), so it is shown on every view rather than risk
// hiding it from the one slice that actually caused it.
export const isRowInView = (row, view, lapsingEnabled) => {
    if (view === "overall") return true;
    if (!lapsingEnabled) return false;
    if (row.demandUnknown) return true;
    if (view === "new") return (row.committedDemand || 0) > 0;
    return (row.confidentDemand || 0) > 0 || (row.speculativeDemand || 0) > 0;
};

// The view's own purchase figure — read from the backend's precomputed `purchase`
// object, never recomputed here. Falls back to the legacy single-figure fields for
// Overall when the lapsing flag is off (pre-Phase-7 behaviour, unchanged). Only Overall
// ever carries a `strips` key — New/Reorder are unrounded tablet gaps, no strip concept.
export const getViewPurchase = (row, view, lapsingEnabled) => {
    if (lapsingEnabled && row.purchase) return row.purchase[view];
    if (view === "overall") return { strips: row.strips, tablets: row.tabletsToOrder };
    return { tablets: null };
};

// "Cumulative qty needed" (Overall/New) / "Total quantity of medicines needed" (Reorder)
// — prototype-matched (§Phase-7-UI). Deliberately includes the SAME velocity weighting
// the purchase formula itself applies to B (row.velocity multiplies speculativeDemand
// only, never confidentDemand — §2's "why v=1.0" note) so this number is "demand before
// the +1 strip buffer", not a second, disagreeing demand figure — Overall's value is
// exactly purchase.overall's gap terms with S subtracted back out; New's and Reorder's
// are exactly their own purchase gap terms before their own buffer treatment.
export const getViewDemand = (row, view, lapsingEnabled) => {
    if (!lapsingEnabled) return row.demand;
    const velocity = row.velocity ?? 1;
    if (view === "new") return row.committedDemand || 0;
    if (view === "reorder") return (row.confidentDemand || 0) + velocity * (row.speculativeDemand || 0);
    return (row.committedDemand || 0) + (row.confidentDemand || 0) + velocity * (row.speculativeDemand || 0);
};

// Prototype-matched "short by N" subtext (Qty present column, Overall/New only — Reorder's
// equivalent column has no such subtext in the target column set) — compares a view's own
// demand number against onHand ALONE, never netting inTransit (matching the prototype's
// own qtyPresent/cumulativeQtyNeeded semantics exactly; inTransit is shown as its own
// column instead, kept deliberately — see ProcurementView.jsx's header comment).
export const getShortfall = (row, view, lapsingEnabled) => Math.max(getViewDemand(row, view, lapsingEnabled) - (row.onHand || 0), 0);

// §Phase-7-UI SOURCE COLUMN — derived on the frontend, no new backend field. Overall only.
export const getSource = (row) => {
    const hasNew = (row.committedDemand || 0) > 0;
    const hasReorder = (row.confidentDemand || 0) + (row.speculativeDemand || 0) > 0;
    if (hasNew && hasReorder) return "New · Reorder";
    if (hasNew) return "New";
    if (hasReorder) return "Reorder";
    return null;
};

export const STATUS_BADGE_CLASSES = {
    Pending: "bg-[var(--ops-carbon-100)] text-[var(--ops-carbon-700)]",
    Requested: "bg-[var(--ops-azure-100)] text-[var(--ops-azure-600)]",
    Delayed: "bg-[var(--ops-amber-50)] text-[var(--ops-amber-700)]",
};

// "Show only shortages" — medicines whose demand in this view exceeds the stock present
// (the same test as the "short by N" subtext). Client-side: it only narrows the rows already
// loaded on the current page, so it costs no extra eVital reads.
export function ShortagesOnlyToggle({ checked, onChange }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm text-[var(--ops-carbon-900)] cursor-pointer">
            <input type="checkbox" className="w-4 h-4" checked={checked} onChange={(event) => onChange(event.target.checked)} />
            Show only shortages
        </label>
    );
}
ShortagesOnlyToggle.propTypes = { checked: PropTypes.bool.isRequired, onChange: PropTypes.func.isRequired };

export function LookaheadSelect({ value, onChange }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm text-[var(--ops-carbon-900)]">
            Look ahead
            <select
                className="h-8 rounded-lg border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] px-2 text-sm"
                value={value}
                onChange={(event) => onChange(parseInt(event.target.value, 10))}
                aria-label="Days to look ahead for reorders"
            >
                {LOOKAHEAD_OPTIONS.map((days) => (
                    <option key={days} value={days}>{`${days} days`}</option>
                ))}
            </select>
        </label>
    );
}
LookaheadSelect.propTypes = { value: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired };

export function PageSizeSelect({ value, onChange }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm text-[var(--ops-carbon-700)]">
            Rows per page
            <select
                className="h-8 rounded-lg border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] px-2 text-sm"
                value={value}
                onChange={(event) => onChange(parseInt(event.target.value, 10))}
            >
                {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                ))}
            </select>
        </label>
    );
}
PageSizeSelect.propTypes = { value: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired };

export function StatusBadge({ status }) {
    return (
        <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.Pending
                }`}
        >
            {status}
        </span>
    );
}
StatusBadge.propTypes = { status: PropTypes.string };

// Row actions: "Add to cart" puts the medicine in the procurement cart straight away, with the
// listing's suggested quantity (`cartQuantity`); quantities and the vendor are set in the cart, for
// all medicines at once. "Receive" appears only while the medicine has open purchase-order lines. "Receive" appears
// only while the medicine has open purchase-order lines (row.openOrderLines), never based
// on status text.
export function RowAction({ row, onReceive, cartQuantity, view, lapsingEnabled, lookaheadDays }) {
    const showReceive = (row.openOrderLines || []).length > 0;
    const cart = useProcurementCart();
    const inCart = cart.has(row.itemId);
    return (
        <div className="flex flex-wrap items-center gap-2">
            <button
                type="button"
                disabled={!row.itemId}
                title={row.itemId ? undefined : "This medicine isn't linked to an item in PillUp, so it can't be ordered."}
                onClick={() => (inCart ? cart.remove(row.itemId) : cart.add(row, cartQuantity, { view, lapsingEnabled, lookaheadDays }))}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${inCart
                        ? "bg-[var(--ops-emerald-100)] text-[var(--ops-emerald-600)] border-[var(--ops-emerald-200)]"
                        : "border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] text-[var(--ops-carbon-900)] hover:bg-[var(--ops-emerald-50)] hover:border-[var(--ops-emerald-200)]"
                    }`}
            >
                {inCart ? "✓ In cart · Remove" : "Add to cart"}
            </button>
            {showReceive && (
                <button
                    type="button"
                    onClick={() => onReceive(row)}
                    className="px-3 py-1.5 rounded-lg bg-[var(--ops-emerald-100)] text-[var(--ops-emerald-600)] border border-[var(--ops-emerald-200)] text-xs font-semibold"
                >
                    Receive
                </button>
            )}
        </div>
    );
}
RowAction.propTypes = {
    row: PropTypes.object.isRequired,
    onReceive: PropTypes.func.isRequired,
    cartQuantity: PropTypes.number,
    view: PropTypes.string,
    lapsingEnabled: PropTypes.bool,
    lookaheadDays: PropTypes.number,
};

// Searchable (type to filter by vendor name). Portalled to <body> so the menu isn't clipped
// by the modal's scroll area; z-index must clear Modal.jsx's overlay (z-[200000]).
const vendorSelectStyles = {
    control: (base) => ({ ...base, minHeight: 36, borderRadius: 8, borderColor: "var(--ops-carbon-200)", fontSize: 14 }),
    menuPortal: (base) => ({ ...base, zIndex: 300000 }),
};

export function VendorSelect({ row, value, onChange, disabled, vendors }) {
    const options = vendors.map((vendor) => ({ value: String(vendor.id), label: vendor.name }));
    const selected = options.find((option) => option.value === String(value)) || null;
    return (
        <Select
            className="w-full max-w-[320px]"
            styles={vendorSelectStyles}
            options={options}
            value={selected}
            onChange={(option) => onChange(row.itemId, option ? option.value : "")}
            isDisabled={disabled}
            isClearable
            isSearchable
            placeholder="Search vendor…"
            noOptionsMessage={() => "No vendor found"}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            aria-label="Vendor"
        />
    );
}
VendorSelect.propTypes = {
    row: PropTypes.object.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    vendors: PropTypes.array,
};

// Under a row's stock figure: opens that medicine's batch-wise stock (BatchStockModal).
export function ViewBatchesButton({ row, onClick }) {
    if (!row.itemInventoryId) return null;
    return (
        <button
            type="button"
            onClick={() => onClick(row)}
            className="mt-0.5 block text-[11px] font-semibold text-[var(--ops-azure-600)] hover:underline"
        >
            View batches
        </button>
    );
}
ViewBatchesButton.propTypes = { row: PropTypes.object.isRequired, onClick: PropTypes.func.isRequired };

export function LedgerLink({ row }) {
    if (!row.ledgerUrl) return <EmptyValue />;
    return (
        <a
            href={row.ledgerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-[var(--ops-emerald-600)] hover:underline"
            onClick={(event) => event.stopPropagation()}
        >
            View ledger →
        </a>
    );
}
LedgerLink.propTypes = { row: PropTypes.object.isRequired };

export function OrderIdPills({ orderIds }) {
    return (
        <div>
            <div className="flex flex-wrap gap-1.5">
                {(orderIds || []).map((orderId) => (
                    <span
                        key={orderId}
                        className="inline-flex items-center rounded-full bg-[var(--ops-azure-100)] text-[var(--ops-azure-600)] border border-[var(--ops-azure-200)] px-2.5 py-1 text-xs font-semibold"
                    >
                        {orderId}
                    </span>
                ))}
            </div>
            <div className="text-[10px] text-[var(--ops-carbon-500)]">
                {(orderIds || []).length} order{(orderIds || []).length === 1 ? "" : "s"}
            </div>
        </div>
    );
}
OrderIdPills.propTypes = { orderIds: PropTypes.array };

// §Phase-7-UI (2026-09-07) — the "Purchase qty" cell, now an editable free-form TABLETS
// input pre-filled with the suggested figure, not a static number. Splitting one
// medicine across vendors needs NO editor of its own: order less than suggested from
// vendor A, the row's in-transit rises and its suggestion recomputes to the remainder on
// the next read, order that from vendor B. This component is deliberately the ONLY way
// to change what gets POSTed — a second, disagreeing multi-vendor-line UI is exactly
// what the product decision here rules out.
// Whole-strip readout (2026-09-08 correction) — vendors sell whole strips, so a
// fractional strip count ("2.33 strips") reads oddly. Always whole strips plus a called-
// out loose-tablet remainder; NEVER a fraction, whether or not tablets divides evenly.
export const stripsReadout = (tablets, stripSize) => {
    if (!stripSize || !Number.isFinite(tablets) || tablets < 0) return null;
    const wholeStrips = Math.floor(tablets / stripSize);
    const loose = tablets % stripSize;
    const stripsLabel = `${wholeStrips} strip${wholeStrips === 1 ? "" : "s"}`;
    return loose === 0 ? stripsLabel : `${stripsLabel} + ${loose} loose`;
};

// The table's purchase cell (2026-10-27): the suggested quantity, read-only. It's worked out
// from the listing's bulk stock total; CreatePurchaseOrderModal recomputes it from the
// medicine's batches and is where it can be changed.
export function SuggestedQuantity({ row, suggested, showBufferNote }) {
    const tablets = Number(suggested);
    if (!Number.isFinite(tablets)) return <EmptyValue />;
    const readout = stripsReadout(tablets, row.stripSize);
    return (
        <div>
            <div className="font-semibold">{tablets}</div>
            {readout != null && <div className="text-[10px] text-[var(--ops-carbon-500)]">{readout}</div>}
            {showBufferNote && (
                <div className="text-[10px] text-[var(--ops-carbon-500)]">
                    {row.stripSize ? `incl. 1 spare strip (${row.stripSize})` : "incl. 1 spare strip"}
                </div>
            )}
        </div>
    );
}
SuggestedQuantity.propTypes = {
    row: PropTypes.object.isRequired,
    suggested: PropTypes.number,
    showBufferNote: PropTypes.bool,
};

// §Phase-7-UI (2026-09-09) — manual strip-size entry. Replaces the Purchase qty cell
// entirely (not shown alongside it) when row.stripSize is null: measured in production,
// 2,302 of 3,339 items with live demand have items.size NULL and are unorderable as a
// result (no strip size means no buffer, no rounding, no suggested quantity —
// calculateShortages flags these rows stockUnknown for exactly this reason, among
// others; see §3.2). Submitting PATCHes items.size ONLY (setItemStripSize,
// pillup-api) and refetches — this component never computes or previews a suggestion
// itself, since the formula lives in calculateShortages alone.
export function StripSizeInput({ row, onSet, isSaving }) {
    const [draft, setDraft] = useState("");
    const hasBeenEdited = draft !== "";
    const parsed = parseInt(draft, 10);
    const isValid = Number.isInteger(parsed) && parsed > 0 && !String(draft).includes(".");
    const showError = hasBeenEdited && !isValid;

    const submit = () => {
        if (!isValid || isSaving) return;
        onSet(row, parsed);
    };

    return (
        <div>
            <div className="flex items-center gap-1.5">
                <input
                    type="number"
                    min="1"
                    step="1"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") submit();
                    }}
                    placeholder="Strip size"
                    disabled={isSaving}
                    className={`w-24 h-8 border rounded-lg px-2 text-sm ${showError ? "border-[var(--ops-crimson-400)]" : "border-[var(--ops-carbon-200)]"
                        }`}
                    aria-label={`Set strip size for ${row.medicineName || "this medicine"}`}
                />
                <button
                    type="button"
                    onClick={submit}
                    disabled={!isValid || isSaving}
                    className="px-3 py-1.5 rounded-lg bg-[var(--ops-emerald-100)] text-[var(--ops-emerald-600)] border border-[var(--ops-emerald-200)] text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? "Saving…" : "Set"}
                </button>
            </div>
            {showError ? (
                <div className="text-[10px] text-[var(--ops-crimson-600)]">Enter a positive whole number</div>
            ) : (
                <div className="text-[10px] text-[var(--ops-carbon-500)]">Strip size unknown — set it to enable ordering</div>
            )}
        </div>
    );
}
StripSizeInput.propTypes = {
    row: PropTypes.object.isRequired,
    onSet: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
};

// Required help text (§Phase-7-UI, 2026-09-07) — splitting one medicine across vendors
// works sequentially through in-transit netting and needs no split-control UI. Shown
// near the bulk/per-row purchase actions on every view, so a pharmacist who orders less
// than the suggestion knows to just re-check the row after a refresh rather than look
// for a way to add a second vendor line to the same order.
export const SPLIT_VENDOR_COPY =
    "Splitting one medicine across vendors needs no extra step: order less than the suggested quantity, refresh, and the row recomputes to what's still outstanding — order that from the next vendor.";

export function SplitVendorNotice() {
    return <p className="mt-2 text-[11px] text-[var(--ops-carbon-500)]">{SPLIT_VENDOR_COPY}</p>;
}

// Required on New and Reorder (§Phase-7-UI) — both are PARTIAL views: each nets the full
// stock independently, so their figures do NOT sum to Overall (onHand 10 / N 30 / reorder
// 60 -> New 30, Reorder 50, Overall 90). Ordering from New then Reorder and seeing both
// reach zero does not mean the medicine is covered — the figures do converge (every view
// subtracts in-transit, so each pass recomputes), but only if Overall is checked last.
export const PARTIAL_VIEW_COPY =
    "Partial view — this demand only, netted against all stock. Check Overall for the complete requirement.";

export function PartialViewNotice() {
    return <p className="mt-3 text-xs text-[var(--ops-carbon-500)]">{PARTIAL_VIEW_COPY}</p>;
}

// Why a row's stock is unknown — `row.stockUnknownReason` (pillup-api
// classes/InventoryProcurement/Constants.js STOCK_UNKNOWN_REASONS). NO_STRIP_SIZE never
// reaches the badge: a missing strip size renders StripSizeInput instead. An unknown or
// absent reason (older API) falls back to the generic "Stock unknown".
const STOCK_UNKNOWN_COPY = {
    EVITAL_RATE_LIMITED: {
        label: "eVital limit hit",
        title: "eVital's hourly API limit was reached, so this medicine's stock couldn't be read. Refresh after the hour resets.",
    },
    INVALID_MEDICINE_KEY: {
        label: "Not found in eVital",
        title: "eVital doesn't recognise this medicine's id. Check the item's eVital mapping.",
    },
};
const GENERIC_STOCK_UNKNOWN = { label: "Stock unknown", title: "eVital couldn't return stock for this medicine." };

// The pill shown in place of a purchase figure when a row can't be computed.
export function UnavailableBadge({ row }) {
    const stockCopy = row.stockUnknown ? STOCK_UNKNOWN_COPY[row.stockUnknownReason] || GENERIC_STOCK_UNKNOWN : null;
    let label = "Demand unknown";
    if (row.stockUnknown && row.demandUnknown) {
        label = stockCopy === GENERIC_STOCK_UNKNOWN ? "Stock and demand unknown" : `${stockCopy.label} · demand unknown`;
    } else if (row.stockUnknown) {
        label = stockCopy.label;
    }
    return (
        <span
            title={stockCopy?.title}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--ops-amber-50)] text-[var(--ops-amber-700)] text-xs font-semibold"
        >
            {label}
        </span>
    );
}

UnavailableBadge.propTypes = {
    row: PropTypes.shape({
        stockUnknown: PropTypes.bool,
        demandUnknown: PropTypes.bool,
        stockUnknownReason: PropTypes.string,
    }).isRequired,
};

// One line above the table when stock is missing for a reason the pharmacist should know
// about, instead of leaving them to infer it from a column of identical pills.
export function StockHealthNotice({ rows }) {
    const rateLimited = rows.filter((row) => row.stockUnknownReason === "EVITAL_RATE_LIMITED").length;
    if (!rateLimited) return null;

    return (
        <div className="mt-3 rounded-xl border border-[var(--ops-amber-200)] bg-[var(--ops-amber-50)] px-4 py-2.5 text-xs text-[var(--ops-amber-700)]">
            {`Stock for ${rateLimited} medicine${rateLimited === 1 ? "" : "s"} on this page couldn't be read because eVital's hourly API limit was reached. Refresh after the hour resets.`}
        </div>
    );
}

// The paginated screen shows eVital's bulk TOTAL (`stockExact: false`): one eVital call per
// page, but it can't leave out batches about to expire. Said once, above the table.
export function StockTotalNotice({ rows }) {
    if (!rows.some((row) => row.stockExact === false)) return null;
    return (
        <p className="mt-3 text-xs text-[var(--ops-carbon-500)]">
            Qty present is eVital&apos;s total stock, including batches that expire within 7 days. The info button on a cart line shows the batch-wise stock and works out the quantity without those batches.
        </p>
    );
}

StockTotalNotice.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

StockHealthNotice.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};
